"use client"

import * as React from "react"

import { BranchType } from "@ibada/shared-types/common"

import { useTranslations } from "@/i18n"
import { useFinanceFeatureQuery } from "@/features/finance/services/finance-query"
import { FinanceOverviewCardId } from "@/features/finance/model/finance-normalized-overview"
import { findOverviewCard, readMoney } from "@/features/finance/lib/finance-overview-integration"
import type { FinanceOverviewQuery } from "@/features/finance/model/finance-types"
import { getBranchDisplayLabel } from "@/shared/lib/branch/branch-display"

import {
  useFinanceOverviewPeriod,
  useFinanceOverviewScope,
} from "@/features/finance/overview/context/finance-overview-context"

export interface GlobalBranchRow {
  id: string
  name: string
  income: string
  expense: string
  net: string
  netIsNegative: boolean
  /** True when the branch recorded no money at all this period — neither in nor out. */
  isSilent: boolean
}

export interface GlobalBranchRowsResult {
  rows: GlobalBranchRow[]
  /**
   * Church-wide income for the period, as the SERVER computed it.
   *
   * The denominator for every share below. Adding the branch rows up in the browser would be a second,
   * competing definition of the workspace total (ADR-0011) — and it would do it by summing floats, which
   * IBA-116 exists to prevent.
   */
  totalIncome: string
  /** Church-wide expense for the period, as the SERVER computed it. Denominator for spend shares. */
  totalExpense: string
  currency: string
  /**
   * True when branch autonomy withholds the absolutes (ADR-0024): every money cell above is then the server's
   * share of its column, in percent, and must never be formatted as money.
   */
  figuresAreRatios: boolean
  isLoading: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => void
}

/**
 * Per-branch income/expense/net for the consolidated board.
 *
 * One hook because several widgets ask different questions of the SAME server table — who earns most, who is
 * carrying the church, who has gone quiet. React Query dedupes them to a single request, and more importantly
 * they cannot disagree: three widgets each mapping the payload separately is how two of them end up naming a
 * different top branch.
 *
 * Rows arrive already totalled and sorted by the backend. Nothing is summed here — ADR-0011 puts authoritative
 * aggregates on the server, and re-adding them on the client is exactly the drift it exists to prevent.
 */
export function useGlobalBranchRows(
  /**
   * Another period than the board's (the F3 tiles' previous-period fallback), and whether to ask at all. The read
   * goes to the same endpoint, so it passes the same gate and disclosure as the board's own.
   */
  options: { period?: { from: string; to: string }; enabled?: boolean } = {},
): GlobalBranchRowsResult {
  const t = useTranslations("finance.overview")
  const scope = useFinanceOverviewScope()
  const boardPeriod = useFinanceOverviewPeriod()
  const period = options.period ?? boardPeriod
  const enabled = options.enabled ?? true
  const { useFinanceOverviewQuery } = useFinanceFeatureQuery()

  const query = useFinanceOverviewQuery(
    enabled && scope.workspaceId && period.from && period.to
      ? ({
          workspaceId: scope.workspaceId,
          context: scope.overviewContext,
          dateFrom: period.from,
          dateTo: period.to,
          // No `profile`: the dashboard profile trims breakdowns, and these widgets ARE a breakdown.
        } satisfies FinanceOverviewQuery)
      : null,
  )

  const rows = React.useMemo<GlobalBranchRow[]>(() => {
    const table = query.data?.tables?.find((entry) => entry.id === "branchPerformance")
    return (table?.rows ?? []).map((row) => {
      const record = row as Record<string, unknown>
      const name = typeof record.name === "string" ? record.name : ""
      const net = moneyCell(record.balance)
      const income = moneyCell(record.totalIncome)
      const expense = moneyCell(record.totalExpense)
      return {
        id: typeof record.id === "string" ? record.id : name,
        // The system branch is provisioned as `SYSTEM_BRANCH_<workspace>`; to a treasurer it is the Parish.
        name: getBranchDisplayLabel({
          branchType: name.startsWith("SYSTEM_BRANCH_") ? BranchType.System : BranchType.Normal,
          branchName: name,
          parishLabel: t("widgets.branchPerformance.parish"),
          fallbackLabel: name,
        }),
        income,
        expense,
        net,
        netIsNegative: net.trim().startsWith("-"),
        isSilent: isZeroMoney(income) && isZeroMoney(expense),
      }
    })
  }, [query.data, t])

  return {
    rows,
    // `readMoney` yields a DisplayAmount (string | number); shares only ever divide it, so normalise to the
    // canonical string form the rest of this module speaks.
    totalIncome: String(
      readMoney(findOverviewCard(query.data?.cards ?? [], FinanceOverviewCardId.TotalIncome)?.value) ?? "0",
    ),
    totalExpense: String(
      readMoney(findOverviewCard(query.data?.cards ?? [], FinanceOverviewCardId.TotalExpenses)?.value) ?? "0",
    ),
    currency: query.data?.meta?.currency ?? scope.currency,
    figuresAreRatios: query.data?.meta?.figuresAreRatios === true,
    isLoading: query.isLoading,
    isError: query.isError,
    isFetching: query.isFetching,
    refetch: () => void query.refetch(),
  }
}

/**
 * A money cell as its canonical string. Money arrives as a decimal string; under autonomy the server replaces it
 * with a share in percent, which is a JSON number. Reading only strings turned every share into "0", which made
 * every branch look silent on the withheld board.
 */
function moneyCell(value: unknown): string {
  if (typeof value === "string") return value
  if (typeof value === "number" && Number.isFinite(value)) return String(value)
  return "0"
}

/**
 * Whether a canonical decimal string is zero.
 *
 * String-based on purpose (IBA-116): money crosses the wire as a decimal string and `Number("0.00")` round-trips
 * fine today but stops being safe the moment a figure exceeds float precision. Stripping sign, separators and
 * zeros answers the question without ever making it a float.
 */
function isZeroMoney(value: string): boolean {
  return value.replace(/[-+0.,\s]/g, "") === ""
}

/** Share of a total, as a percentage, from two server-provided figures. Presentation only — never a total. */
export function shareOfTotal(value: string, total: string): number {
  const totalNumber = Number(total)
  if (!Number.isFinite(totalNumber) || totalNumber === 0) return 0
  const valueNumber = Number(value)
  if (!Number.isFinite(valueNumber)) return 0
  return (valueNumber / totalNumber) * 100
}
